/**
 * CST to AST Visitor for Natural DSL
 *
 * Transforms Chevrotain's CST into our Zod-validated AST
 */
import { IToken, CstNode, CstChildrenDictionary } from 'chevrotain';
import { parser } from './grammar';
import {
  createFreeText,
  createSpace,
  createHighlightedText,
  createColorText,
  createBoldText,
  createStringInput,
  createNumericInput,
  createFraction,
  createImage,
  createVisualFraction,
  createInlineDragOption,
  createDisplayClock,
  createInteractiveClock,
  CLOCK_TIME_REGEX,
  CLOCK_TYPES,
  CLOCK_MODES,
  CLOCK_FORMATS,
  ClockType,
  ClockMode,
  ClockFormat,
  InlineNode,
  type VisualFractionInline,
  type VisualFractionShape,
  type ColorTextInline,
  type BoldTextInline,
  type HighlightedTextInline,
} from '../ast/inline';
import {
  createTextBlock,
  createLineBlock,
  TextBlock,
  LineBlock
} from '../ast/blocks';
import {
  createNumberLineBlock,
  createNumberLinePosition,
  createNumberLineCounter,
  createNumberLineHighlight,
  createNumberLineCursor,
  createNumberLineArrow,
  NumberLineBlock,
  NumberLinePosition,
  NumberLineCounter,
  NumberLineHighlight,
  NumberLineCursor,
  NumberLineArrow
} from '../ast/number-line';
import type { NumberLineHighlightColor, NumberLineDragMode } from '../constants/number-line-constants';
import {
  createTableBlock,
  createTableRow,
  createTableCell,
  TableBlock,
  TableRow,
  TableCell
} from '../ast/table';
import {
  createVerticalOperationBlock,
  createVopRow,
  createVopCarryRow,
  createVopSeparator,
  createVopDigitCell,
  createVopLabelCell,
  createVopInputCell,
  createVopEmptyCell,
  createVopColorCell,
  createVopBoldCell,
  createVopHighlightCell,
  normalizeVopOperator,
  VerticalOperationBlock,
  VopBlockItem,
  VopRow,
  VopCarryRow,
  VopSeparator,
  VopCell
} from '../ast/vertical-operation';
import type { ContentBlock } from '../ast/document';
import {
  createMatcherBlock,
  createMatcherRow,
  MatcherBlock,
  MatcherRow,
} from '../ast/matcher';
import {
  createMatcherItem,
  MatcherItem,
  DisplayInlineNode,
} from '../ast/matcher-item';
import {
  createPairMatcherBlock,
  PairMatcherBlock,
} from '../ast/pair-matcher';
import {
  createDragOptions,
  createNumericKeypad,
  createRulerTool,
  createProtractorTool,
  createSetSquareTool,
  ToolNode
} from '../ast/tools';
import {
  createRadioOptions,
  RadioOptionsBlock
} from '../ast/radio-options';
import {
  createSimpleValidation,
  createExpressionValidation,
  createMixedValidation,
  ValidationNode
} from '../ast/validation';
import {
  createExerciseDocument,
  ExerciseDocument
} from '../ast/document';
import { NumberLineStyle, NumberLineLabels } from '../constants';

/** Union of possible DSL parameter values */
type ParamValue = string | number | boolean | [number, number] | string[] | number[];

// Get base visitor
const BaseCstVisitor = parser.getBaseCstVisitorConstructor();

/**
 * Visitor that transforms CST nodes into AST nodes
 */
class NaturalDSLVisitor extends BaseCstVisitor {
  constructor() {
    super();
    this.validateVisitor();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Top-level Document
  // ═══════════════════════════════════════════════════════════════════════════

  exerciseDocument(ctx: CstChildrenDictionary): ExerciseDocument {
    const question: TextBlock | undefined = ctx.question
      ? this.visit(ctx.question as CstNode[])
      : undefined;
    const contentBlocks: ContentBlock[] = ctx.contentBlock
      ? (ctx.contentBlock as CstNode[]).map((cb) => this.visit(cb))
      : [];
    const tools: ToolNode[] = ctx.tool
      ? (ctx.tool as CstNode[]).map((t) => this.visit(t))
      : [];
    const validation: ValidationNode | undefined = ctx.validation
      ? this.visit(ctx.validation as CstNode[])
      : undefined;

    return createExerciseDocument(question, contentBlocks, validation, tools);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Question Block
  // ═══════════════════════════════════════════════════════════════════════════

  question(ctx: CstChildrenDictionary): TextBlock {
    const valueToken = ctx.value![0] as IToken;
    const value = this.extractString(valueToken.image);
    return createTextBlock(value);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Content Block
  // ═══════════════════════════════════════════════════════════════════════════

  contentBlock(ctx: CstChildrenDictionary): ContentBlock {
    if (ctx.table) return this.visit(ctx.table as CstNode[]);
    if (ctx.radioButtons) return this.visit(ctx.radioButtons as CstNode[]);
    if (ctx.numberLine) return this.visit(ctx.numberLine as CstNode[]);
    if (ctx.pairMatcher) return this.visit(ctx.pairMatcher as CstNode[]);
    if (ctx.matcher) return this.visit(ctx.matcher as CstNode[]);
    if (ctx.verticalOperation) return this.visit(ctx.verticalOperation as CstNode[]);
    if (ctx.line) return this.visit(ctx.line as CstNode[]);
    throw new Error('Unknown content block');
  }

  radioButtons(ctx: CstChildrenDictionary): RadioOptionsBlock {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;
    const answer = params.answer as string;
    const options = params.options as string[];
    return createRadioOptions(name, options, answer);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Table Block
  // ═══════════════════════════════════════════════════════════════════════════

  table(ctx: CstChildrenDictionary): TableBlock {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const cols = params.cols as number;
    const rows: TableRow[] = (ctx.row as CstNode[]).map((r) => this.visit(r));
    return createTableBlock(cols, rows);
  }

  row(ctx: CstChildrenDictionary): TableRow {
    const cells: TableCell[] = (ctx.cell as CstNode[]).map((c) => this.visit(c));
    return createTableRow(cells);
  }

  cell(ctx: CstChildrenDictionary): TableCell {
    const params = ctx.parameterList
      ? this.visit(ctx.parameterList as CstNode[])
      : {};
    const colspan = params.colspan as number | undefined;
    const inlines: InlineNode[] = ctx.inlineElement
      ? (ctx.inlineElement as CstNode[]).map((elem) => this.visit(elem))
      : [];
    return createTableCell(inlines, colspan);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Number Line Block
  // ═══════════════════════════════════════════════════════════════════════════

  numberLine(ctx: CstChildrenDictionary): NumberLineBlock {
    const params = this.visit(ctx.parameterList as CstNode[]);

    if (params.min === undefined || params.max === undefined) {
      throw new Error('NUMBER_LINE requires parameters: min and max');
    }

    const min        = Number(params.min);
    const max        = Number(params.max);
    const step       = params.step       !== undefined ? Number(params.step)       : undefined;
    const minorTicks = params.minorTicks !== undefined ? Number(params.minorTicks) : undefined;
    const style             = params.style          as NumberLineStyle  | undefined;
    const labelVisibility  = params.labelVisibility as NumberLineLabels | undefined;
    const dragMode         = params.dragMode        as NumberLineDragMode | undefined;

    const positions: NumberLinePosition[] = ctx.position
      ? (ctx.position as CstNode[]).map((p) => this.visit(p))
      : [];

    const rawCounters = ctx.counter as CstNode[] | undefined;
    if (rawCounters && rawCounters.length > 1) {
      throw new Error('NUMBER_LINE: only one COUNTER block is allowed');
    }
    const counter: NumberLineCounter | undefined = rawCounters
      ? this.visit(rawCounters)
      : undefined;

    const highlights: NumberLineHighlight[] = ctx.highlight
      ? (ctx.highlight as CstNode[]).map((h) => this.visit(h))
      : [];

    const cursors: NumberLineCursor[] = ctx.cursor
      ? (ctx.cursor as CstNode[]).map((c) => this.visit(c))
      : [];

    const arrows: NumberLineArrow[] = ctx.arrow
      ? (ctx.arrow as CstNode[]).map((a) => this.visit(a))
      : [];

    return createNumberLineBlock(min, max, positions, step, minorTicks, style, labelVisibility, counter, highlights, cursors.length > 0 ? cursors : undefined, arrows.length > 0 ? arrows : undefined, dragMode);
  }

  position(ctx: CstChildrenDictionary): NumberLinePosition {
    const params = this.visit(ctx.parameterList as CstNode[]);

    if (params.value === undefined) {
      throw new Error('POSITION requires parameter: value');
    }

    const value = Number(params.value);

    const inlines: InlineNode[] = ctx.inlineElement
      ? (ctx.inlineElement as CstNode[]).map((elem) => this.visit(elem))
      : [];

    return createNumberLinePosition(value, inlines);
  }

  counter(ctx: CstChildrenDictionary): NumberLineCounter {
    const params = this.visit(ctx.parameterList as CstNode[]);

    if (params.max === undefined) {
      throw new Error('COUNTER requires parameter: max');
    }

    const max          = Number(params.max);
    const min          = params.min     !== undefined ? Number(params.min)     : undefined;
    const defaultValue = params.default !== undefined ? Number(params.default) : undefined;
    const name         = params.name    as string | undefined;
    const answer       = params.answer  !== undefined ? Number(params.answer)  : undefined;

    return createNumberLineCounter(max, min, defaultValue, name, answer);
  }

  highlight(ctx: CstChildrenDictionary): NumberLineHighlight {
    const params = this.visit(ctx.parameterList as CstNode[]);

    if (params.from === undefined || params.to === undefined) {
      throw new Error('HIGHLIGHT requires parameters: from and to');
    }

    const from  = Number(params.from);
    const to    = Number(params.to);
    const color = params.color as NumberLineHighlightColor | undefined;
    const label = params.label as string | undefined;

    return createNumberLineHighlight(from, to, color, label);
  }

  cursor(ctx: CstChildrenDictionary): NumberLineCursor {
    const params = this.visit(ctx.parameterList as CstNode[]);

    if (params.answer === undefined) {
      throw new Error('CURSOR requires parameter: answer');
    }

    const answer    = Number(params.answer);
    const value     = params.value     !== undefined ? Number(params.value)     : undefined;
    const color     = params.color     as string | undefined;
    const fillRange = params.fillRange !== undefined
      ? params.fillRange === true || params.fillRange === 'true'
      : undefined;
    const label     = params.label     as string | undefined;

    return createNumberLineCursor(answer, value, color, fillRange, label);
  }

  arrow(ctx: CstChildrenDictionary): NumberLineArrow {
    const params = this.visit(ctx.parameterList as CstNode[]);

    if (params.from === undefined || params.to === undefined) {
      throw new Error('ARROW requires parameters: from and to');
    }

    const from  = Number(params.from);
    const to    = Number(params.to);
    const color = params.color as string | undefined;
    const inlines: InlineNode[] = ctx.inlineElement
      ? (ctx.inlineElement as CstNode[]).map((elem) => this.visit(elem))
      : [];

    return createNumberLineArrow(from, to, inlines, color);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Matcher Block
  // ═══════════════════════════════════════════════════════════════════════════

  matcher(ctx: CstChildrenDictionary): MatcherBlock {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;
    const colors = params.colors as string[] | undefined;
    const rows: MatcherRow[] = (ctx.matcherRow as CstNode[]).map(r => this.visit(r));
    const answers: string[][] = this.visit(ctx.matcherAnswers as CstNode[]);
    return createMatcherBlock(name, rows, answers, colors);
  }

  matcherRow(ctx: CstChildrenDictionary): MatcherRow {
    const items: MatcherItem[] = (ctx.matcherItem as CstNode[]).map(i => this.visit(i));
    return createMatcherRow(items);
  }

  matcherItem(ctx: CstChildrenDictionary): MatcherItem {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const itemId = params.id as string;
    // Grammar gates out `inlineInput` inside ITEM (displayOnly=true), so every
    // visited node is a DisplayInlineNode; AST-level DisplayInlineNodeSchema
    // enforces the invariant as a safety net.
    const inlines: DisplayInlineNode[] = ctx.inlineElement
      ? (ctx.inlineElement as CstNode[]).map(e => this.visit(e) as DisplayInlineNode)
      : [];
    return createMatcherItem(itemId, inlines);
  }

  matcherAnswers(ctx: CstChildrenDictionary): string[][] {
    return ctx.matcherAnswerSet
      ? (ctx.matcherAnswerSet as CstNode[]).map(s => this.visit(s))
      : [];
  }

  matcherAnswerSet(ctx: CstChildrenDictionary): string[] {
    return ctx.ids
      ? (ctx.ids as IToken[]).map(t => t.image)
      : [];
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Pair Matcher Block
  // ═══════════════════════════════════════════════════════════════════════════

  pairMatcher(ctx: CstChildrenDictionary): PairMatcherBlock {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;

    const topItems: MatcherItem[] = this.visit(ctx.pairMatcherTopSection as CstNode[]);
    const bottomItems: MatcherItem[] = this.visit(ctx.pairMatcherBottomSection as CstNode[]);
    const rawAnswers: string[][] = this.visit(ctx.matcherAnswers as CstNode[]);
    return createPairMatcherBlock(name, topItems, bottomItems, rawAnswers as [string, string][]);
  }

  pairMatcherTopSection(ctx: CstChildrenDictionary): MatcherItem[] {
    return this.visitMatcherItemList(ctx);
  }

  pairMatcherBottomSection(ctx: CstChildrenDictionary): MatcherItem[] {
    return this.visitMatcherItemList(ctx);
  }

  /**
   * Maps `ctx.matcherItem` CST nodes to MatcherItem AST nodes.
   * Shared by pairMatcherTopSection and pairMatcherBottomSection because their
   * grammar rules are structurally identical — both are `matcherItem+` lists
   * wrapped in a labelled section keyword.
   */
  private visitMatcherItemList(ctx: CstChildrenDictionary): MatcherItem[] {
    return ctx.matcherItem
      ? (ctx.matcherItem as CstNode[]).map(i => this.visit(i))
      : [];
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Vertical Operation Block
  // ═══════════════════════════════════════════════════════════════════════════

  verticalOperation(ctx: CstChildrenDictionary): VerticalOperationBlock {
    const params = ctx.parameterList ? this.visit(ctx.parameterList as CstNode[]) : {};
    const op = params.op !== undefined ? normalizeVopOperator(String(params.op)) : undefined;

    // Rebuild source order via CST startOffset so rows + seps interleave correctly.
    const tagged: Array<{ offset: number; node: VopBlockItem }> = [];
    (ctx.vopRow as CstNode[] | undefined)?.forEach((cstNode, i) => {
      tagged.push({ offset: cstNode.location?.startOffset ?? i, node: this.visit(cstNode) });
    });
    (ctx.vopCarry as CstNode[] | undefined)?.forEach((cstNode, i) => {
      tagged.push({ offset: cstNode.location?.startOffset ?? i, node: this.visit(cstNode) });
    });
    (ctx.vopSep as CstNode[] | undefined)?.forEach((cstNode, i) => {
      tagged.push({ offset: cstNode.location?.startOffset ?? i, node: this.visit(cstNode) });
    });
    tagged.sort((a, b) => a.offset - b.offset);
    const items = tagged.map((t) => t.node);

    return createVerticalOperationBlock(op, items);
  }

  vopRow(ctx: CstChildrenDictionary): VopRow {
    const cells: VopCell[] = ctx.vopCell
      ? (ctx.vopCell as CstNode[]).map((c) => this.visit(c))
      : [];
    const rowParams = ctx.parameterList ? this.visit(ctx.parameterList as CstNode[]) : {};
    const op = rowParams.op !== undefined ? normalizeVopOperator(String(rowParams.op)) : undefined;
    return createVopRow(cells, op);
  }

  vopCarry(ctx: CstChildrenDictionary): VopCarryRow {
    const cells: VopCell[] = ctx.vopCell
      ? (ctx.vopCell as CstNode[]).map((c) => this.visit(c))
      : [];
    return createVopCarryRow(cells);
  }

  vopSep(_ctx: CstChildrenDictionary): VopSeparator {
    return createVopSeparator();
  }

  vopCell(ctx: CstChildrenDictionary): VopCell {
    if (ctx.NumberLiteral) {
      const token = ctx.NumberLiteral[0] as IToken;
      return createVopDigitCell(token.image);
    }
    if (ctx.StringLiteral) {
      const token = ctx.StringLiteral[0] as IToken;
      const value = token.image.slice(1, -1); // strip quotes
      return createVopLabelCell(value);
    }
    if (ctx.Underscore) {
      return createVopEmptyCell();
    }
    if (ctx.INPUT) {
      const params = this.visit(ctx.parameterList as CstNode[]);
      const name = params.name as string | undefined;
      if (!name) {
        throw new Error('INPUT inside VERTICAL_OPERATION requires parameter: name');
      }
      const digits = params.digits !== undefined ? Number(params.digits) : 1;
      const answer = params.answer as string | number | undefined;
      return createVopInputCell(name, digits, answer);
    }
    if (ctx.colorText) {
      const inline = this.visit(ctx.colorText as CstNode[]) as ColorTextInline;
      return createVopColorCell(inline.value, inline.color);
    }
    if (ctx.boldText) {
      const inline = this.visit(ctx.boldText as CstNode[]) as BoldTextInline;
      return createVopBoldCell(inline.value);
    }
    if (ctx.textHighlighter) {
      const inline = this.visit(ctx.textHighlighter as CstNode[]) as HighlightedTextInline;
      return createVopHighlightCell(inline.value);
    }
    throw new Error('Unknown vopCell');
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Line Block
  // ═══════════════════════════════════════════════════════════════════════════

  line(ctx: CstChildrenDictionary): LineBlock {
    const inlines: InlineNode[] = (ctx.inlineElement as CstNode[]).map((elem) => this.visit(elem));
    return createLineBlock(inlines);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Inline Elements
  // ═══════════════════════════════════════════════════════════════════════════

  inlineElement(ctx: CstChildrenDictionary): InlineNode {
    if (ctx.FreeText) {
      const token = ctx.FreeText[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.Identifier) {
      const token = ctx.Identifier[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.NumberLiteral) {
      const token = ctx.NumberLiteral[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.StringLiteral) {
      const token = ctx.StringLiteral[0] as IToken;
      // Remove quotes from string
      const value = token.image.slice(1, -1);
      return createFreeText(value);
    }

    if (ctx.Space) {
      // Space token (one or more spaces becomes a single Space element)
      return createSpace();
    }

    // Operators
    if (ctx.Plus) {
      const token = ctx.Plus[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.Minus) {
      const token = ctx.Minus[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.Multiply) {
      const token = ctx.Multiply[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.Divide) {
      const token = ctx.Divide[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.Equals) {
      const token = ctx.Equals[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.LessThan) {
      const token = ctx.LessThan[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.GreaterThan) {
      const token = ctx.GreaterThan[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.Question) {
      const token = ctx.Question[0] as IToken;
      return createFreeText(token.image);
    }

    // Punctuation
    if (ctx.Comma) {
      const token = ctx.Comma[0] as IToken;
      return createFreeText(token.image);
    }

    // Parentheses
    if (ctx.LParen) {
      const token = ctx.LParen[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.RParen) {
      const token = ctx.RParen[0] as IToken;
      return createFreeText(token.image);
    }

    if (ctx.textHighlighter) {
      return this.visit(ctx.textHighlighter as CstNode[]);
    }

    if (ctx.colorText) {
      return this.visit(ctx.colorText as CstNode[]);
    }

    if (ctx.boldText) {
      return this.visit(ctx.boldText as CstNode[]);
    }

    if (ctx.fraction) {
      return this.visit(ctx.fraction as CstNode[]);
    }

    if (ctx.image) {
      return this.visit(ctx.image as CstNode[]);
    }

    if (ctx.visualFraction) {
      return this.visit(ctx.visualFraction as CstNode[]);
    }

    if (ctx.inlineInput) {
      return this.visit(ctx.inlineInput as CstNode[]);
    }

    if (ctx.inlineDragOption) {
      return this.visit(ctx.inlineDragOption as CstNode[]);
    }

    if (ctx.clock) {
      return this.visit(ctx.clock as CstNode[]);
    }

    throw new Error('Unknown inline element');
  }

  // Manual checks below duplicate what ClockInlineSchema.parse() enforces, but produce
  // DSL-friendly error messages (e.g. 'CLOCK: value is required') instead of Zod paths.
  clock(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]) as Record<string, unknown>;

    const mode = (params.mode ?? 'display') as ClockMode;
    if (!(CLOCK_MODES as readonly string[]).includes(mode)) {
      throw new Error(`CLOCK: mode must be one of ${CLOCK_MODES.join(', ')} (got "${mode}")`);
    }

    const clockType = params.type as ClockType;
    if (!clockType) {
      throw new Error('CLOCK: type is required ("analog" or "digital")');
    }
    if (!(CLOCK_TYPES as readonly string[]).includes(clockType)) {
      throw new Error(`CLOCK: type must be one of ${CLOCK_TYPES.join(', ')} (got "${clockType}")`);
    }

    const value = params.value as string;
    if (!value) {
      throw new Error('CLOCK: value is required ("HH:MM" 24h format)');
    }
    if (!CLOCK_TIME_REGEX.test(value)) {
      throw new Error(`CLOCK: value must be "HH:MM" in 24h format (got "${value}")`);
    }

    const format = params.format as ClockFormat | undefined;
    if (format !== undefined && !(CLOCK_FORMATS as readonly string[]).includes(format)) {
      throw new Error(`CLOCK: format must be one of ${CLOCK_FORMATS.join(', ')} (got "${format}")`);
    }

    const name = params.name as string | undefined;
    const answer = params.answer as string | undefined;
    const snap = params.snap as number | undefined;

    if (mode === 'display') {
      if (answer !== undefined) {
        throw new Error('CLOCK: "answer" is only valid when mode="interactive"');
      }
      if (snap !== undefined) {
        throw new Error('CLOCK: "snap" is only valid when mode="interactive"');
      }
      return createDisplayClock({ clockType, value, format, name });
    }

    // mode === 'interactive'
    if (clockType !== 'analog') {
      throw new Error('CLOCK: interactive mode requires type="analog" (digital clocks cannot be interactive)');
    }
    if (answer === undefined) {
      throw new Error('CLOCK: interactive mode requires "answer"');
    }
    if (!CLOCK_TIME_REGEX.test(answer)) {
      throw new Error(`CLOCK: answer must be "HH:MM" in 24h format (got "${answer}")`);
    }
    if (!name) {
      throw new Error('CLOCK: interactive mode requires "name" (for answer validation)');
    }
    if (snap !== undefined && (!Number.isInteger(snap) || snap < 1 || snap > 60)) {
      throw new Error(`CLOCK: snap must be an integer in 1..60 (got ${snap})`);
    }

    return createInteractiveClock({ name, value, answer, format, snap });
  }

  textHighlighter(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const value = params.value as string;

    // Convert start and end parameters to highlight tuple
    let highlight: [number, number] | undefined = undefined;
    if (params.start !== undefined && params.end !== undefined) {
      highlight = [params.start as number, params.end as number];
    }

    return createHighlightedText(value, highlight);
  }

  colorText(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const value = params.value as string;
    const color = params.color as string;

    let highlight: [number, number] | undefined = undefined;
    if (params.start !== undefined && params.end !== undefined) {
      highlight = [params.start as number, params.end as number];
    }

    return createColorText(value, color, highlight);
  }

  boldText(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const value = params.value as string;

    let highlight: [number, number] | undefined = undefined;
    if (params.start !== undefined && params.end !== undefined) {
      highlight = [params.start as number, params.end as number];
    }

    return createBoldText(value, highlight);
  }

  fraction(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;
    const numerator = params.numerator as number | undefined;
    const denominator = params.denominator as number | undefined;
    const answer = params.answer as string | undefined;
    const simplified = params.simplified !== undefined
      ? (params.simplified as boolean)
      : undefined;
    const placeholder = params.placeholder as string | undefined;

    return createFraction(name, { numerator, denominator, answer, simplified, placeholder });
  }

  image(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;
    const src = params.src as string;
    const alt = params.alt as string;
    const width = params.width as number | undefined;
    const height = params.height as number | undefined;
    return createImage(name, src, alt, { width, height });
  }
  visualFraction(ctx: CstChildrenDictionary): VisualFractionInline {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;
    const divisions = params.divisions as number;
    const shape = params.shape as VisualFractionShape | undefined;
    const filled = (params.filled as number[] | undefined) ?? [];
    const answer = (params.answer as number[] | undefined) ?? [];
    const orientation = params.orientation as 'horizontal' | 'vertical' | undefined;
    const label = params.label !== undefined ? (params.label as string) : undefined;
    const size = params.size !== undefined ? (params.size as number) : undefined;

    return createVisualFraction(name, divisions, { shape, filled, answer, orientation, label, size });
  }

  numberArray(ctx: CstChildrenDictionary): number[] {
    return ctx.items
      ? (ctx.items as IToken[]).map(t => parseFloat(t.image))
      : [];
  }

  inlineInput(ctx: CstChildrenDictionary): InlineNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const name = params.name as string;
    const answer = params.answer as string | number | undefined;
    const placeholder = params.placeholder as string | undefined;

    // W_S_I / STRING_INPUT uses "length" parameter for string inputs
    if (ctx.stringInput) {
      const length = params.length as number;
      return createStringInput(name, length, answer, placeholder);
    }

    // W_N_I / NUMERIC_INPUT uses "digits" parameter for numeric inputs
    const digits = params.digits as number;
    return createNumericInput(name, digits, answer, placeholder);
  }

  inlineDragOption(ctx: CstChildrenDictionary): InlineNode {
    const valueToken = (ctx.StringLiteral as IToken[])[0]!;
    const value = this.extractString(valueToken.image);
    return createInlineDragOption(value);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Tools
  // ═══════════════════════════════════════════════════════════════════════════

  tool(ctx: CstChildrenDictionary): ToolNode {
    if (ctx.dragableButtons) {
      return this.visit(ctx.dragableButtons as CstNode[]);
    }

    if (ctx.numericKeypad) {
      return this.visit(ctx.numericKeypad as CstNode[]);
    }

    if (ctx.rulerTool) {
      return this.visit(ctx.rulerTool as CstNode[]);
    }

    if (ctx.protractorTool) {
      return this.visit(ctx.protractorTool as CstNode[]);
    }

    if (ctx.setSquareTool) {
      return this.visit(ctx.setSquareTool as CstNode[]);
    }

    throw new Error('Unknown tool');
  }

  dragableButtons(ctx: CstChildrenDictionary): ToolNode {
    const strings = this.visit(ctx.stringList as CstNode[]);
    return createDragOptions(strings);
  }

  numericKeypad(ctx: CstChildrenDictionary): ToolNode {
    const params = ctx.parameterList
      ? this.visit(ctx.parameterList as CstNode[])
      : {};
    const includeOperators = params.includeOperators === true || params.includeOperators === 'true';
    const includeDecimal = params.includeDecimal === true || params.includeDecimal === 'true';
    const style = params.style as 'diamond' | 'classic' | undefined;
    return createNumericKeypad(includeOperators || undefined, includeDecimal || undefined, style);
  }

  rulerTool(ctx: CstChildrenDictionary): ToolNode {
    const params = ctx.parameterList
      ? this.visit(ctx.parameterList as CstNode[])
      : {};
    const size = params.size !== undefined ? Number(params.size) : undefined;
    return createRulerTool(size);
  }

  protractorTool(ctx: CstChildrenDictionary): ToolNode {
    const params = ctx.parameterList
      ? this.visit(ctx.parameterList as CstNode[])
      : {};
    const size = params.size !== undefined ? Number(params.size) : undefined;
    return createProtractorTool(size);
  }

  setSquareTool(ctx: CstChildrenDictionary): ToolNode {
    const params = ctx.parameterList
      ? this.visit(ctx.parameterList as CstNode[])
      : {};
    const size = params.size !== undefined ? Number(params.size) : undefined;
    return createSetSquareTool(size);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Validation
  // ═══════════════════════════════════════════════════════════════════════════

  validation(ctx: CstChildrenDictionary): ValidationNode {
    const params = this.visit(ctx.parameterList as CstNode[]);
    const type = params.type as string;

    if (type === 'simple') {
      return createSimpleValidation();
    } else if (type === 'expression') {
      const expression = params.expression as string;
      return createExpressionValidation(expression);
    } else if (type === 'mixed') {
      const expression = params.expression as string | undefined;
      if (!expression || expression.trim() === '') {
        throw new Error('Mixed validation requires a non-empty expression');
      }
      return createMixedValidation(expression);
    }

    throw new Error(`Unknown validation type: ${type}`);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Parameter Lists
  // ═══════════════════════════════════════════════════════════════════════════

  parameterList(ctx: CstChildrenDictionary): Record<string, ParamValue> {
    const result: Record<string, ParamValue> = {};

    if (ctx.parameter) {
      (ctx.parameter as CstNode[]).forEach((param) => {
        const [key, value] = this.visit(param);
        result[key] = value;
      });
    }

    return result;
  }

  parameter(ctx: CstChildrenDictionary): [string, ParamValue] {
    const key = (ctx.key![0] as IToken).image;

    // Extract value
    let value: ParamValue | undefined;

    if (ctx.value) {
      const valueToken = ctx.value[0] as IToken;

      // Check if it's a token with tokenType property
      if (valueToken && valueToken.tokenType && valueToken.tokenType.name) {
        // Check token type
        if (valueToken.tokenType.name === 'StringLiteral') {
          value = this.extractString(valueToken.image);
        } else if (valueToken.tokenType.name === 'NumberLiteral') {
          value = parseFloat(valueToken.image);
        } else if (valueToken.tokenType.name === 'Identifier') {
          // Boolean or identifier
          const id = valueToken.image;
          if (id === 'true') value = true;
          else if (id === 'false') value = false;
          else value = id;
        } else {
          // Unknown token type
          value = valueToken.image;
        }
      } else {
        // Tuple or other CST node
        value = this.visit(ctx.value as CstNode[]);
      }
    }

    return [key, value!];
  }

  tuple(ctx: CstChildrenDictionary): [number, number] {
    const start = parseFloat((ctx.start![0] as IToken).image);
    const end = parseFloat((ctx.end![0] as IToken).image);
    return [start, end];
  }

  stringList(ctx: CstChildrenDictionary): string[] {
    return ctx.StringLiteral!.map((token) => this.extractString((token as IToken).image));
  }

  curlyStringList(ctx: CstChildrenDictionary): string[] {
    return this.visit(ctx.stringList as CstNode[]);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Helpers
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Extract string content from quoted string token
   */
  private extractString(quotedString: string): string {
    return quotedString.slice(1, -1); // Remove quotes
  }
}

// Export singleton visitor instance
export const visitor = new NaturalDSLVisitor();
